import React, {
    Dispatch,
    ReactNode,
    SetStateAction,
    useEffect,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { flowRight, template } from "lodash";
import parse from "html-react-parser";
import { ItemDataType } from "rsuite/esm/internals/types";
import {
    Checkbox,
    CheckPicker,
    Form,
    FormInstance,
    HStack,
    List,
    Message,
    Schema,
    Text,
    toaster,
} from "rsuite";

import constants from "utils/constants";

import { AppDispatch, RootState } from "store/store";
import {
    getActiveEnvironmentId,
    getActiveEnvironmentMoniker,
} from "store/environments/environment-selectors";
import {
    getActiveStep,
    getEnrollCache,
    getEnrollId,
} from "store/environments/tenants/enroll-tenant/enroll-tenant-selectors";
import { getCustomerDetails } from "store/customers/customer-details/customer-details-selectors";
import { setActiveStepData } from "store/environments/tenants/enroll-tenant/enroll-tenant-actions";
import { setIsUnsavedChangeAvailable } from "store/common/unsaved-data/unsaved-data-actions";
import {
    EnrollCache,
    EnrollStep,
    FirstAdminData,
    StepStatus,
} from "store/environments/tenants/enroll-tenant/enroll-tenant-types";
import { AuthenticationType } from "store/environments/tenants/tenant-configurations/tenant-configurations-types";
import { User } from "store/environments/tenants/users/users-types";
import {
    CustomerType,
    ICustomerDetails,
} from "store/customers/customer-details/customer-details-types";

import { fetchEnrollCache } from "store/environments/tenants/enroll-tenant/enroll-tenant-thunks";
import { getCustomerDetailsWithoutLoader as fetchCustomerDetails } from "store/customers/customer-details/customer-details-thunk";

import { getErrorMessage } from "api/defaults";
import { loadAuthenticationMethods } from "api/environments/tenants/tenant-erp-configurations-api";
import {
    loadUsers as loadUsersForD365,
    validateIntegrationUser as validateIntegrationUserForD365,
} from "api/environments/tenants/users/erp/users-api";
import {
    loadUsers,
    validateIntegrationUser,
} from "api/environments/tenants/users/users-api";

import useRefreshPage from "hooks/useRefreshPage";

import withLoader, { WithLoaderProps } from "hocs/withLoader/withLoader";

import RefreshButton from "components/Common/RefreshButton/RefreshButton";

import "./SetAdmin.less";

type FirstAdminFormValue = {
    isD365RoleDooapRequired: boolean;
    users: User[];
    integrationUser?: User;
};

const defaultFirstAdminFormValue: FirstAdminFormValue = {
    isD365RoleDooapRequired: false,
    users: [],
};

const useAppDispatch = () => useDispatch<AppDispatch>();

interface Props extends WithLoaderProps {
    setIsStepFormValid: Dispatch<SetStateAction<() => boolean>>;
}

const SetAdmin: React.FC<Props> = ({
    setIsStepFormValid,
    showLoader,
}: Props) => {
    const { t } = useTranslation();

    const formRef = useRef<FormInstance<Record<string, any>> | null>(null);

    const dispatch = useAppDispatch();

    const environmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const environmentMoniker = useSelector((state: RootState): string =>
        getActiveEnvironmentMoniker(state)
    );

    const enrollId = useSelector((state: RootState): string =>
        getEnrollId(state)
    );

    const activeStep = useSelector((state: RootState): EnrollStep | null =>
        getActiveStep(state)
    );

    const enrollCache = useSelector((state: RootState): EnrollCache | null =>
        getEnrollCache(state)
    );

    const customerDetails = useSelector(
        (state: RootState): ICustomerDetails | null => getCustomerDetails(state)
    );

    const [isFormDisabled, setIsFormDisabled] = useState(false);
    const [userListData, setUserListData] = useState<ItemDataType<any>[]>([]);
    const [firstAdminValue, setFirstAdminValue] = useState<FirstAdminFormValue>(
        defaultFirstAdminFormValue
    );
    const [isFetchInProgress, setIsFetchInProgress] = useState<boolean>(false);
    const [isUsersLoading, setIsUsersLoading] = useState<boolean>(false);
    const [isIntegrationUserValid, setIsIntegrationUserValid] =
        useState<boolean>(true);
    const [isPasswordAuthentication, setIsPasswordAuthentication] = useState<
        boolean | null
    >(null);

    type formValueType = keyof FirstAdminFormValue;

    const formFieldKeys: Record<string, formValueType> = {
        users: "users",
    };

    const isStepRunning = activeStep?.status === StepStatus.Running;
    const isD365Customer = customerDetails?.type === CustomerType.D365;

    const instruction = firstAdminValue.isD365RoleDooapRequired
        ? t("enrollTenant.setAdmin.instructionCase1")
        : t("enrollTenant.setAdmin.instructionCase2");

    const integrationUserMessage = firstAdminValue.integrationUser
        ? template(t("enrollTenant.setAdmin.integrationUser"))({
              name: firstAdminValue.integrationUser.axId,
              email: firstAdminValue.integrationUser.userPrincipalName,
          })
        : null;

    const model = Schema.Model({
        users: Schema.Types.ArrayType()
            .isRequired(
                t("enrollTenant.stepFormCommon.validationMessages.isRequired")
            )
            .addRule((value) => {
                return value.every(
                    (user: User) => user.axId !== "" && user.axId !== null
                );
            }, t("enrollTenant.stepFormCommon.validationMessages.axIdIsRequired")),
    });

    const fetchData = () => {
        const promises: any[] = [];

        if (!enrollCache || !enrollCache?.authenticationMethod) {
            promises.push(
                dispatch(fetchEnrollCache(environmentMoniker, enrollId))
            );
        }

        if (!customerDetails?.type && enrollCache?.customerId) {
            promises.push(
                dispatch(fetchCustomerDetails(enrollCache.customerId))
            );
        }

        if (promises.length > 0) {
            setIsFetchInProgress(true);
            showLoader(true);

            Promise.all(promises).then(() => {
                showLoader(false);
                setIsFetchInProgress(false);
            });
        }
    };

    const handleUserFetch = (
        payload: ReadonlyArray<User> | null,
        status: number
    ) => {
        if (payload && status === constants.statusCode.OK) {
            setUserListData(
                payload.map((user) => ({
                    label: user.name || "",
                    value: { ...user },
                }))
            );
        } else {
            const errorMessage = getErrorMessage(String(payload), status);
            const errorAlert = template(
                t("enrollTenant.setAdmin.errorMessageTemplate")
            )({
                error: errorMessage,
            });

            toaster.push(
                <Message type="error" showIcon>
                    {errorAlert}
                </Message>,
                {
                    duration: constants.errorAlertDurationMsec,
                }
            );
        }

        setIsUsersLoading(false);
    };

    const handleNotificationOpen = () => {
        toaster.push(
            <Message type="warning" showIcon closable>
                <Text>
                    {t(
                        "enrollTenant.setAdmin.integrationUserWrongDomainMessage"
                    )}
                </Text>
            </Message>
        );
    };

    const handleFormDataChange = (userFormData: Record<string, any>) => {
        const newUserFormData = userFormData as FirstAdminFormValue;

        if (newUserFormData) {
            setFirstAdminValue(newUserFormData);
            dispatch(setActiveStepData(JSON.stringify(newUserFormData ?? "")));
        }

        dispatch(setIsUnsavedChangeAvailable(true));
    };

    const handleDooapD365RoleRequiredChange = (checked: boolean) => {
        dispatch(setIsUnsavedChangeAvailable(true));
        setFirstAdminValue({
            ...firstAdminValue,
            isD365RoleDooapRequired: checked,
        });
    };

    const handleRefreshClick = () => {
        setFirstAdminValue({
            ...firstAdminValue,
            users: [],
        });
        setUserListData([]);
    };

    const renderDropdownItem = (label: ReactNode, item: ItemDataType<any>) => {
        return (
            <div key={item.value.id}>
                <div>{label}</div>
                <div>{item.value.userPrincipalName}</div>
            </div>
        );
    };

    const searchByHandler = (
        keyword: string,
        label: ReactNode,
        item: ItemDataType<any>
    ) => {
        const query = keyword.toLowerCase();

        return (
            item.value?.userPrincipalName?.toLowerCase().includes(query) ||
            label?.toString()?.toLowerCase().includes(query)
        );
    };

    useRefreshPage(() => {
        if (enrollId) {
            if (activeStep?.status !== StepStatus.Success) {
                setFirstAdminValue(defaultFirstAdminFormValue);
                setUserListData([]);
                toaster.clear();
            }

            formRef.current?.cleanErrors();
        }
    });

    useEffect(() => {
        setIsStepFormValid(() => () => formRef?.current?.check() || false);
    }, [setIsStepFormValid, formRef]);

    useEffect(() => {
        if (environmentMoniker && enrollId && !isFetchInProgress) {
            fetchData();
        }
    }, [environmentMoniker, enrollId, isFetchInProgress, fetchData]);

    useEffect(() => {
        if (
            isStepRunning &&
            environmentMoniker &&
            enrollCache?.tenantId &&
            enrollCache?.authenticationMethod &&
            isPasswordAuthentication === null
        ) {
            loadAuthenticationMethods(
                environmentMoniker,
                enrollCache?.tenantId
            ).then(([payload, statusCode]) => {
                if (payload && statusCode === constants.statusCode.OK) {
                    const isPasswordAuthentication =
                        payload.find(
                            (authMethod) =>
                                authMethod.authenticationDescription ===
                                enrollCache.authenticationMethod
                        )?.authenticationType ===
                        AuthenticationType.UsernamePassword;

                    setIsPasswordAuthentication(isPasswordAuthentication);
                }
            });
        }
    }, [
        isStepRunning,
        enrollCache?.tenantId,
        environmentMoniker,
        isPasswordAuthentication,
        enrollCache?.authenticationMethod,
    ]);

    useEffect(() => {
        if (
            isStepRunning &&
            environmentId &&
            enrollCache?.tenantId &&
            customerDetails?.type &&
            isPasswordAuthentication
        ) {
            const validate = isD365Customer
                ? validateIntegrationUserForD365
                : validateIntegrationUser;

            validate(environmentId, enrollCache?.tenantId).then(
                ([payload, status]) => {
                    if (
                        payload !== null &&
                        status === constants.statusCode.OK
                    ) {
                        setIsIntegrationUserValid(payload);
                    }
                }
            );
        }
    }, [
        isStepRunning,
        enrollCache?.tenantId,
        environmentId,
        customerDetails,
        isD365Customer,
        isPasswordAuthentication,
    ]);

    useEffect(() => {
        if (
            isStepRunning &&
            environmentId &&
            enrollCache?.tenantId &&
            customerDetails?.type &&
            !userListData.length
        ) {
            setIsUsersLoading(true);

            const load = isD365Customer ? loadUsersForD365 : loadUsers;

            load(environmentId, enrollCache?.tenantId).then(
                ([payload, statusCode]) => {
                    handleUserFetch(payload?.data ?? [], statusCode);
                }
            );
        }
    }, [
        isStepRunning,
        enrollCache?.tenantId,
        environmentId,
        customerDetails,
        isD365Customer,
        userListData,
    ]);

    useEffect(() => {
        if (!isIntegrationUserValid && !isUsersLoading) {
            handleNotificationOpen();
        }
    }, [isIntegrationUserValid, isUsersLoading]);

    useEffect(() => {
        toaster.clear();
    }, []);

    useEffect(() => {
        if (activeStep) {
            try {
                const stepHistory: FirstAdminData = JSON.parse(
                    activeStep.data ?? ""
                );

                const integrationUserIndex = stepHistory.users.findIndex(
                    (user) => user.isIntegrationUser
                );

                let integrationUser;
                if (integrationUserIndex !== -1) {
                    const integrationUsers = stepHistory.users.splice(
                        integrationUserIndex,
                        1
                    );
                    integrationUser = integrationUsers[0];
                }

                if (stepHistory) {
                    setFirstAdminValue({
                        isD365RoleDooapRequired:
                            stepHistory.isD365RoleDooapRequired,
                        users: stepHistory.users,
                        integrationUser: integrationUser,
                    });

                    if (activeStep?.status === StepStatus.Success) {
                        setUserListData(
                            stepHistory.users.map((user) => ({
                                label: user.name || "",
                                value: { ...user },
                            }))
                        );
                        setIsFormDisabled(true);
                    }
                }
            } catch {
                return;
            }
        }
    }, []);

    useEffect(() => {
        if (isStepRunning && firstAdminValue) {
            dispatch(setActiveStepData(JSON.stringify(firstAdminValue ?? "")));
        }
    }, [isStepRunning, firstAdminValue]);

    return (
        activeStep && (
            <Form
                className={"set-admin__form"}
                ref={formRef}
                model={model}
                formValue={firstAdminValue}
                onChange={handleFormDataChange}
            >
                {isD365Customer ? (
                    <>
                        <Text className={"set-admin__form__text"}>
                            {t(
                                "enrollTenant.setAdmin.requireD365role.description"
                            )}
                        </Text>

                        <Checkbox
                            className={"set-admin__form__checkbox"}
                            checked={firstAdminValue.isD365RoleDooapRequired}
                            disabled={isFormDisabled}
                            onChange={(_, checked) =>
                                handleDooapD365RoleRequiredChange(checked)
                            }
                        >
                            {t("enrollTenant.setAdmin.requireD365role.title")}
                        </Checkbox>

                        <Text className={"set-admin__form__text"}>
                            {instruction}
                        </Text>
                    </>
                ) : (
                    <Text className={"set-admin__form__text"}>
                        {t("enrollTenant.setAdmin.instructionCase3")}
                    </Text>
                )}

                <Form.Group className={"set-admin__form__group"}>
                    <Form.ControlLabel className={"set-admin__form__label"}>
                        {t("enrollTenant.setAdmin.userTitle")}
                    </Form.ControlLabel>

                    {isStepRunning ? (
                        <HStack justifyContent={"flex-start"} spacing={20}>
                            <HStack.Item alignSelf={"center"}>
                                <Form.Control
                                    className={"set-admin__form__control"}
                                    accepter={CheckPicker}
                                    size="lg"
                                    data={userListData}
                                    renderMenuItem={renderDropdownItem}
                                    searchBy={searchByHandler}
                                    virtualized={false}
                                    disabled={isFormDisabled || isUsersLoading}
                                    name={formFieldKeys.users}
                                    placeholder={t(
                                        "enrollTenant.setAdmin.userPlaceholder"
                                    )}
                                />
                            </HStack.Item>

                            <HStack.Item alignSelf={"center"}>
                                <RefreshButton
                                    loading={isUsersLoading}
                                    tooltip={t(
                                        "enrollTenant.setAdmin.refreshTooltip"
                                    )}
                                    onClick={handleRefreshClick}
                                />
                            </HStack.Item>
                        </HStack>
                    ) : (
                        <>
                            <List
                                className={"set-admin__form__users"}
                                size="sm"
                            >
                                {firstAdminValue.users.map(
                                    (user: User, index: number) => (
                                        <List.Item key={String(index)}>
                                            <Text muted>{user.name} </Text>

                                            <Text muted>
                                                {user.userPrincipalName}
                                            </Text>
                                        </List.Item>
                                    )
                                )}
                            </List>

                            {integrationUserMessage && (
                                <Text className={"set-admin__form__text"}>
                                    {parse(integrationUserMessage)}
                                </Text>
                            )}
                        </>
                    )}
                </Form.Group>
            </Form>
        )
    );
};

const Component = flowRight(withLoader)(SetAdmin);

export default Component;
